import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';

export interface WhatsAppConfiguration {
  id: number;
  companyGroupId: number;
  apiUrl: string;
  authHeader: string;
  authScheme: string;
  defaultCountryCode: string;
  languageCode: string;
  timeoutMs: number;
  enabled: boolean;
  hasApiKey: boolean;
  companyGroup?: { name?: string };
  createdByUser?: { FullName?: string; EmployeeCode?: string };
  updatedByUser?: { FullName?: string; EmployeeCode?: string };
  createdAt: string;
  updatedAt: string;
}

export interface WhatsAppConfigurationPayload {
  companyGroupId: number;
  apiUrl: string;
  apiKey?: string;
  authHeader: string;
  authScheme: string;
  defaultCountryCode: string;
  languageCode: string;
  timeoutMs: number;
  enabled: boolean;
}

interface WhatsAppConfigurationQuery {
  page: number;
  pageSize: number;
  search?: string;
}

const whatsappConfigApi = createApi({
  reducerPath: 'whatsappConfigApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['WhatsAppConfiguration'],
  endpoints: builder => ({
    getWhatsAppConfigurations: builder.query<
      BackendResponse<{ count: number; list: WhatsAppConfiguration[] }>,
      WhatsAppConfigurationQuery
    >({
      query: query => ({
        url: `/whatsapp-configuration${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['WhatsAppConfiguration'],
    }),
    createWhatsAppConfiguration: builder.mutation<
      BackendResponse<
        Pick<WhatsAppConfiguration, 'id' | 'companyGroupId' | 'enabled'>
      >,
      WhatsAppConfigurationPayload & { apiKey: string }
    >({
      query: body => ({ url: '/whatsapp-configuration', method: 'POST', body }),
      invalidatesTags: ['WhatsAppConfiguration'],
    }),
    updateWhatsAppConfiguration: builder.mutation<
      BackendResponse<
        Pick<WhatsAppConfiguration, 'id' | 'companyGroupId' | 'enabled'>
      >,
      { id: number; body: WhatsAppConfigurationPayload }
    >({
      query: ({ id, body }) => ({
        url: `/whatsapp-configuration/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['WhatsAppConfiguration'],
    }),
    deleteWhatsAppConfigurations: builder.mutation<
      BackendResponse<void>,
      number[]
    >({
      query: ids => ({
        url: '/whatsapp-configuration',
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: ['WhatsAppConfiguration'],
    }),
  }),
});

export const {
  useGetWhatsAppConfigurationsQuery,
  useCreateWhatsAppConfigurationMutation,
  useUpdateWhatsAppConfigurationMutation,
  useDeleteWhatsAppConfigurationsMutation,
} = whatsappConfigApi;

export default whatsappConfigApi;
